import React, { useState } from "react";
import { Card, CardContent, Button, Badge } from "@/components/ui";
import { Filter, X, ChevronDown, ChevronUp } from "lucide-react";
import SubsidiaryFilter from "./SubsidiaryFilter";
import DateRangeFilter from "./DateRangeFilter";
import AccountFilter from "./AccountFilter";
import BudgetFilter from "./BudgetFilter";
import TransactionTypeFilter from "./TransactionTypeFilter";
import PostingStatusFilter from "./PostingStatusFilter";

/**
 * Filter Panel Component
 *
 * Combines all filter components into a collapsible panel.
 * Shows active filter count and provides apply/clear functionality.
 *
 * Requirements: 5.1-5.10
 */
const FilterPanel = ({
  filters = {},
  onFiltersChange,
  onApply,
  onClear,
  availableFilters = [
    "subsidiary",
    "dateRange",
    "accounts",
    "budgets",
    "transactionTypes",
    "postingStatus",
  ],
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleFilterChange = (filterName, value) => {
    onFiltersChange({
      ...filters,
      [filterName]: value,
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.subsidiary_id) count++;
    if (filters.date_from || filters.date_to) count++;
    if (filters.account_ids?.length > 0) count += filters.account_ids.length;
    if (filters.budget_ids?.length > 0) count += filters.budget_ids.length;
    if (filters.transaction_types?.length > 0)
      count += filters.transaction_types.length;
    if (filters.posting_statuses?.length > 0)
      count += filters.posting_statuses.length;
    return count;
  };

  const activeCount = getActiveFilterCount();

  return (
    <Card className="mb-6">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <h3 className="font-semibold text-lg">Filters</h3>
            {activeCount > 0 && (
              <Badge variant="secondary">{activeCount} active</Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Filter Controls */}
        {isExpanded && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Subsidiary Filter */}
              {availableFilters.includes("subsidiary") && (
                <SubsidiaryFilter
                  value={filters.subsidiary_id}
                  onChange={(value) =>
                    handleFilterChange("subsidiary_id", value)
                  }
                />
              )}

              {/* Date Range Filter */}
              {availableFilters.includes("dateRange") && (
                <DateRangeFilter
                  dateFrom={filters.date_from}
                  dateTo={filters.date_to}
                  onDateFromChange={(value) =>
                    handleFilterChange("date_from", value)
                  }
                  onDateToChange={(value) =>
                    handleFilterChange("date_to", value)
                  }
                />
              )}

              {/* Account Filter */}
              {availableFilters.includes("accounts") && (
                <AccountFilter
                  selectedAccounts={filters.account_ids || []}
                  onChange={(value) => handleFilterChange("account_ids", value)}
                />
              )}

              {/* Budget Filter */}
              {availableFilters.includes("budgets") && (
                <BudgetFilter
                  selectedBudgets={filters.budget_ids || []}
                  onChange={(value) => handleFilterChange("budget_ids", value)}
                  subsidiaryId={filters.subsidiary_id}
                />
              )}

              {/* Transaction Type Filter */}
              {availableFilters.includes("transactionTypes") && (
                <TransactionTypeFilter
                  selectedTypes={filters.transaction_types || []}
                  onChange={(value) =>
                    handleFilterChange("transaction_types", value)
                  }
                />
              )}

              {/* Posting Status Filter */}
              {availableFilters.includes("postingStatus") && (
                <PostingStatusFilter
                  selectedStatuses={filters.posting_statuses || []}
                  onChange={(value) =>
                    handleFilterChange("posting_statuses", value)
                  }
                />
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 pt-4 border-t">
              <Button onClick={onApply} className="flex-1">
                Apply Filters
              </Button>
              <Button
                variant="outline"
                onClick={onClear}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Clear All
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FilterPanel;
